import React from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import HighlightAltIcon from '@mui/icons-material/HighlightAlt';
import DateRangeIcon from '@mui/icons-material/DateRange';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useQueryClient } from 'react-query';
import {
  useGetModelEntityGroupsQuery,
  useCreateModelEntityGroupMutation,
  useUpdateModelEntityGroupMutation,
  FillType,
  ModelEntityGroupDto,
  PumpType,
  refreshMineModel,
  removeAddedNodeMutation,
  useDeleteStopesMutation,
  useRemoveSpoolsMutation
} from 'providers/api';
import { useConfirmation } from 'providers/confirm';
import useRoles from 'hooks/useRoles';
import { i18n } from 'i18n';
import { isAdminUser, isPcUser } from 'utils/roles';
import { Model } from '../../../hooks/useModelState';
import AutoPipeAssignerDialog from '../../../AutoPipeAssigner/AutoPipeAssignerDialog';
import { useMarquee } from '../../../contexts/MarqueeContext';
import { useToolHandlers } from '../../../contexts/ToolHandlerContext';
import useChangePipes from '../../../EditorPanelPage/hooks/useChangePipes';
import useChangePipeBetweenNodes from '../../../EditorPanelPage/hooks/useChangePipeBetweenNodes';
import { useConnectNodes, useDisconnectNodes } from '../../../EditorPanelPage/hooks/useConnectNodes';
import { PCNode } from '../../../MineModel/Nodes';
import PCSpool from '../../../MineModel/Spools/PCSpool';
import PCPipe from '../../../MineModel/Pipes/PCPipe';
import { getBulkEditSceneState, requestCancelTools, updateBulkEditSceneState } from '../bulkEditSceneState';
import { deactivateMeasure } from './measureState';
import ToolbarSection from './ToolbarSection';
import ToolbarButton from './ToolbarButton';
import SplitToolbarButton from './SplitToolbarButton';
import SpoolStatusFromDatesPopout from './SpoolStatusFromDatesPopout';
import {
  filterIdsInModel,
  splitSelectedIdsByType,
  useBulkEditSceneState,
  TOOLBAR_MENU_Z_INDEX
} from './utils';

type PipeChangeMode = 'bulk' | 'spool' | 'between';

interface EditTabProps {
  model: Model;
}

const PIPE_CHANGE_MODE_LABELS: Record<PipeChangeMode, string> = {
  bulk: 'Bulk Change Pipe',
  spool: 'Change Pipe per Spool',
  between: 'Change Pipe Between Nodes',
};

const PIPE_CHANGE_MODE_DESCRIPTIONS: Record<PipeChangeMode, string> = {
  bulk: 'Marquee-select multiple spools at once and apply the selected pipe type to all of them.',
  spool: 'Click each spool individually to apply the selected pipe type. Click again on the toolbar button to stop.',
  between: 'Pick two nodes — every spool between them gets the selected pipe type.',
};

const EditTab = ({ model }: EditTabProps) => {
  const [selectedGroupId, setSelectedGroupId] = React.useState<string>('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);
  const [newGroupName, setNewGroupName] = React.useState('');
  const [selectedPipeType, setSelectedPipeType] = React.useState<string>('');
  const [pipeChangeMode, setPipeChangeMode] = React.useState<PipeChangeMode>('bulk');
  const [isBulkPipeSelecting, setIsBulkPipeSelecting] = React.useState(false);
  const {
    selectedIds, entityType, isSelecting, isDeleting, cancelToolsSignal,
  } = useBulkEditSceneState();
  const confirm = useConfirmation();
  const marquee = useMarquee();
  const tools = useToolHandlers();
  const mineModelId = model.project.entityId;
  const projectId = model.project.entityId as string;
  const modelRef = React.useRef(model);
  modelRef.current = model;
  const marqueeRef = React.useRef(marquee);
  marqueeRef.current = marquee;

  const {
    isSelectingNodes: isChangingPipes,
    handleSelectForChangePipe,
    handleCancelSelection: handleCancelChangePipeSelection,
    handleSpoolSelect: handleChangePipeSpoolSelect,
    handleBulkSpoolsChange,
    invalidateModel: invalidatePipeModel,
  } = useChangePipes({
    model,
    project: model.project,
    selectedPipeType: selectedPipeType || null,
  });

  const {
    isChangingPipeBetweenNodes,
    handleSelectForChangePipeBetweenNodes,
    handleCancelSelection: handleCancelChangePipeBetweenNodesSelection,
    handleNodeSelect: handleChangePipeBetweenNodesSelect,
  } = useChangePipeBetweenNodes({
    model,
    project: model.project,
    selectedPipeType: selectedPipeType || null,
  });

  const {
    isSelectingNodes: isConnectingNodes,
    handleSelectForConnectNode,
    handleCancelSelection: handleCancelConnectSelection,
    handleNodeSelect: handleConnectNodeSelect,
  } = useConnectNodes({ model, project: model.project });

  const {
    isSelectingNodes: isDisconnectingNodes,
    handleSelectForDisconnectNode,
    handleCancelSelection: handleCancelDisconnectSelection,
    handleSpoolSelect: handleDisconnectSpoolSelect,
  } = useDisconnectNodes({ model, project: model.project });

  const { data: groups } = useGetModelEntityGroupsQuery(mineModelId, { enabled: !!mineModelId });
  const createGroupMutation = useCreateModelEntityGroupMutation();
  const updateGroupMutation = useUpdateModelEntityGroupMutation(mineModelId);
  const removeAddedNodes = removeAddedNodeMutation();
  const removeSpoolsMutation = useRemoveSpoolsMutation(projectId);
  const removeStopesMutation = useDeleteStopesMutation();
  const queryClient = useQueryClient();
  const [statusFromDatesOpen, setStatusFromDatesOpen] = React.useState(false);
  const statusFromDatesAnchorRef = React.useRef<HTMLDivElement | null>(null);
  const [autoAssignOpen, setAutoAssignOpen] = React.useState(false);
  const roles = useRoles();
  // The write is an ordinary pipe change any editor may make; the planner itself is a P&C engineering tool.
  const canAutoAssignPipes = isAdminUser(roles) || isPcUser(roles);
  const isPumpedPaste = model.project.pumpType === PumpType.Pump && model.project.fillType !== FillType.Hydraulic;

  const filteredGroups = (groups ?? []).filter(
    (g: ModelEntityGroupDto) => entityType === 'All' || g.groupType === entityType,
  );

  const cancelAllToolModes = React.useCallback(() => {
    if (isChangingPipes) handleCancelChangePipeSelection();
    if (isChangingPipeBetweenNodes) handleCancelChangePipeBetweenNodesSelection();
    if (isConnectingNodes) handleCancelConnectSelection();
    if (isDisconnectingNodes) handleCancelDisconnectSelection();
    if (isBulkPipeSelecting) {
      setIsBulkPipeSelecting(false);
      marquee.setMarqueeMode(false);
      invalidatePipeModel();
    }
  }, [
    isChangingPipes,
    isChangingPipeBetweenNodes,
    isConnectingNodes,
    isDisconnectingNodes,
    isBulkPipeSelecting,
    handleCancelChangePipeSelection,
    handleCancelChangePipeBetweenNodesSelection,
    handleCancelConnectSelection,
    handleCancelDisconnectSelection,
    invalidatePipeModel,
    marquee,
  ]);

  const isPipeToolActive = isChangingPipes || isChangingPipeBetweenNodes || isBulkPipeSelecting;
  const isAnyToolActive = isPipeToolActive || isConnectingNodes || isDisconnectingNodes;

  const initialCancelSignalRef = React.useRef(cancelToolsSignal);
  const cancelAllToolModesRef = React.useRef(cancelAllToolModes);
  cancelAllToolModesRef.current = cancelAllToolModes;

  const showPipeTypeVisuals = React.useCallback(() => {
    model.setSpoolColoursByPipe(true);
  }, [model]);

  const activatePipeChange = (mode: PipeChangeMode) => {
    if (!selectedPipeType) return;
    showPipeTypeVisuals();
    updateBulkEditSceneState({ isSelecting: false });
    if (mode === 'spool') {
      marquee.setMarqueeMode(false);
      handleSelectForChangePipe();
    } else if (mode === 'between') {
      marquee.setMarqueeMode(false);
      handleSelectForChangePipeBetweenNodes();
    } else {
      setIsBulkPipeSelecting(true);
      marquee.setMarqueeMode(true);
    }
  };

  const handleChangePipe = () => {
    if (!selectedPipeType) return;
    if (pipeChangeMode === 'spool') {
      if (isChangingPipes) {
        handleCancelChangePipeSelection();
        return;
      }
    } else if (pipeChangeMode === 'between') {
      if (isChangingPipeBetweenNodes) {
        handleCancelChangePipeBetweenNodesSelection();
        return;
      }
    } else if (isBulkPipeSelecting) {
      setIsBulkPipeSelecting(false);
      marquee.setMarqueeMode(false);
      invalidatePipeModel();
      return;
    }
    cancelAllToolModes();
    activatePipeChange(pipeChangeMode);
  };

  const handlePipeChangeMenuItem = (key: string) => {
    if (key === 'bulk' || key === 'spool' || key === 'between') {
      cancelAllToolModes();
      setPipeChangeMode(key);
      activatePipeChange(key);
    }
  };

  const handleBulkSpoolPipeChange = React.useCallback((spools: PCSpool[]) => {
    if (!selectedPipeType || !model) return;
    handleBulkSpoolsChange(spools);
  }, [selectedPipeType, model, handleBulkSpoolsChange]);

  const handleConnect = () => {
    if (isConnectingNodes) {
      handleCancelConnectSelection();
      return;
    }
    cancelAllToolModes();
    updateBulkEditSceneState({ isSelecting: false });
    marquee.setMarqueeMode(false);
    handleSelectForConnectNode();
  };

  const handleDisconnect = () => {
    if (isDisconnectingNodes) {
      handleCancelDisconnectSelection();
      return;
    }
    cancelAllToolModes();
    updateBulkEditSceneState({ isSelecting: false });
    marquee.setMarqueeMode(false);
    handleSelectForDisconnectNode();
  };

  const handleToggleSelecting = () => {
    if (isDeleting) return;
    const nextSelecting = !isSelecting;
    if (nextSelecting) {
      requestCancelTools();
    }
    updateBulkEditSceneState({ isSelecting: nextSelecting });
    marquee.setMarqueeMode(nextSelecting);
  };

  const deleteSelected = async () => {
    if (isDeleting || selectedIds.length === 0) return;
    const { nodeIds, spoolIds, stopeIds } = splitSelectedIdsByType(model, selectedIds);
    // Removing a node removes its stopes (and refuses if any has pours), so a stope whose node goes too is left to that.
    const nodeIdSet = new Set(nodeIds);
    const stopeEntries = Object.values(model.stopes).flat()
      .filter((s) => s?.data?.stopeId && stopeIds.includes(s.data.stopeId) && s?.originNode?.id && !nodeIdSet.has(s.originNode.id))
      .map((s) => ({ stopeId: s!.data!.stopeId as string, nodeId: s!.originNode!.id as string }));

    try {
      await confirm({
        variant: 'danger',
        description: i18n._('Are you sure you want to delete the selected items?'),
      });
    } catch {
      return;
    }

    updateBulkEditSceneState({ isDeleting: true, isSelecting: false });
    marquee.setMarqueeMode(false);

    try {
      // Every task below mutates the single per-mine reticulation aggregate (optimistic
      // concurrency, checkConcurrency: true). They MUST run sequentially — firing them in
      // parallel makes concurrent requests collide and the later ones throw
      // YesSql.ConcurrencyException ("changed by another process"). Collect thunks (so the
      // requests do not start until awaited) and chain them, swallowing individual failures
      // to preserve the previous allSettled semantics.
      const deletionTasks: (() => Promise<unknown>)[] = [];

      if (stopeEntries.length > 0) {
        deletionTasks.push(() => removeStopesMutation.mutateAsync({
          projectId,
          form: { entries: stopeEntries },
        }));
      }

      if (nodeIds.length > 0) {
        deletionTasks.push(() => removeAddedNodes.mutateAsync({
          projectId,
          nodeId: nodeIds,
          projectName: null,
          data: [],
        }));
      }

      // Spools own the edges and carry real ids, so a bulk disconnect is just those ids in a single
      // batched request (one reticulation load/save) instead of one request per spool.
      const spoolIdsToRemove = spoolIds.filter((spoolId) => !!model.spools[spoolId]);

      if (spoolIdsToRemove.length > 0) {
        deletionTasks.push(() => removeSpoolsMutation.mutateAsync({
          projectId,
          form: { spoolIds: spoolIdsToRemove, connections: [] },
        }));
      }

      await deletionTasks.reduce(
        (chain, task) => chain.then(() => task().catch(() => undefined)),
        Promise.resolve() as Promise<unknown>,
      );
      // Each task's own refetch can start before the next task saves; this one starts after the last.
      refreshMineModel(queryClient);
      updateBulkEditSceneState({ selectedIds: [] });
    } finally {
      updateBulkEditSceneState({ isDeleting: false });
    }
  };

  const handleSelectGroup = () => {
    if (!selectedGroupId) return;
    const group = (groups ?? []).find((g) => g.groupId === selectedGroupId);
    if (!group) return;
    updateBulkEditSceneState({ selectedIds: filterIdsInModel(model, group.memberIds ?? []) });
  };

  const handleAddSelectionToGroup = () => {
    const group = (groups ?? []).find((g) => g.groupId === selectedGroupId);
    if (!group) return;
    const merged = Array.from(new Set([...(group.memberIds ?? []), ...selectedIds]));
    updateGroupMutation.mutate({
      mineModelId,
      groupId: group.groupId ?? '',
      form: { groupName: group.groupName ?? '', memberIds: merged },
    });
  };

  const handleRemoveSelectionFromGroup = () => {
    const group = (groups ?? []).find((g) => g.groupId === selectedGroupId);
    if (!group) return;
    const remaining = (group.memberIds ?? []).filter((id) => !selectedIds.includes(id));
    updateGroupMutation.mutate({
      mineModelId,
      groupId: group.groupId ?? '',
      form: { groupName: group.groupName ?? '', memberIds: remaining },
    });
  };

  const handleOpenCreateDialog = () => {
    if (selectedIds.length === 0) return;
    setNewGroupName('');
    setIsCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setNewGroupName('');
  };

  const handleConfirmCreateGroup = () => {
    if (!newGroupName.trim() || selectedIds.length === 0) return;
    const groupType = entityType === 'All' ? 'Node' : entityType;
    createGroupMutation.mutate({
      mineModelId,
      form: {
        groupName: newGroupName.trim(),
        groupType,
        memberIds: selectedIds,
      },
    });
    handleCloseCreateDialog();
  };

  const handleGroupChange = (e: SelectChangeEvent<string>) => {
    setSelectedGroupId(e.target.value);
  };

  const pipeChangeTooltipTitle = i18n._(PIPE_CHANGE_MODE_LABELS[pipeChangeMode]);
  const pipeChangeDescription = i18n._(PIPE_CHANGE_MODE_DESCRIPTIONS[pipeChangeMode]);

  React.useEffect(() => {
    setSelectedGroupId('');
  }, [entityType]);

  React.useEffect(() => {
    if (isAnyToolActive) {
      updateBulkEditSceneState({ isSelecting: false });
    }
  }, [isAnyToolActive]);

  React.useEffect(() => {
    if (isAnyToolActive || isSelecting) {
      deactivateMeasure();
    }
  }, [isAnyToolActive, isSelecting]);

  React.useEffect(() => {
    if (cancelToolsSignal === initialCancelSignalRef.current) return;
    cancelAllToolModesRef.current();
  }, [cancelToolsSignal]);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (getBulkEditSceneState().isSelecting) {
        updateBulkEditSceneState({ isSelecting: false });
        marquee.setMarqueeMode(false);
      }
      cancelAllToolModesRef.current();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [marquee]);

  React.useEffect(() => {
    if (isBulkPipeSelecting) {
      marquee.setOnNodesSelected(null);
      marquee.setOnSpoolsSelected((spools: PCSpool[]) => {
        handleBulkSpoolPipeChange(spools);
      });
      return () => {
        marquee.setOnNodesSelected(null);
        marquee.setOnSpoolsSelected(null);
      };
    }

    const clickToolActive = isChangingPipes
      || isChangingPipeBetweenNodes
      || isConnectingNodes
      || isDisconnectingNodes;

    if (isSelecting && !clickToolActive) {
      if (entityType === 'Node' || entityType === 'Stope' || entityType === 'All') {
        marquee.setOnNodesSelected((nodes: PCNode[], additive: boolean) => {
          const state = getBulkEditSceneState();
          const m = modelRef.current;
          if (state.entityType === 'Node' || state.entityType === 'All') {
            const nodeIdSet = new Set(nodes.map((n) => n.id));
            const stopeIdsOnNodes = state.entityType === 'All'
              ? Object.values(m.stopes).flat()
                .filter((s) => nodeIdSet.has(s?.originNode?.id))
                .map((s) => s?.data?.stopeId)
                .filter(Boolean) as string[]
              : [];
            const newIds = [...nodes.map((n) => n.id), ...stopeIdsOnNodes];
            const nextIds = additive
              ? Array.from(new Set([...state.selectedIds, ...newIds]))
              : newIds;
            updateBulkEditSceneState({ selectedIds: nextIds });
          } else if (state.entityType === 'Stope') {
            const stopeNodeIdSet = new Set(nodes.filter((n) => n.isStope).map((n) => n.id));
            const allStopes = Object.values(m.stopes).flat();
            const stopeIds = allStopes
              .filter((s) => stopeNodeIdSet.has(s?.originNode?.id))
              .map((s) => s?.data?.stopeId)
              .filter(Boolean) as string[];
            const nextIds = additive
              ? Array.from(new Set([...state.selectedIds, ...stopeIds]))
              : stopeIds;
            updateBulkEditSceneState({ selectedIds: nextIds });
          }
        });
      } else {
        marquee.setOnNodesSelected(null);
      }

      if (entityType === 'Spool' || entityType === 'All') {
        marquee.setOnSpoolsSelected((spools: PCSpool[], additive: boolean) => {
          const state = getBulkEditSceneState();
          const newIds = spools.map((s) => s.spoolId);
          const mergeWithExisting = additive || state.entityType === 'All';
          const nextIds = mergeWithExisting
            ? Array.from(new Set([...state.selectedIds, ...newIds]))
            : newIds;
          updateBulkEditSceneState({ selectedIds: nextIds });
        });
      } else {
        marquee.setOnSpoolsSelected(null);
      }

      return () => {
        marquee.setOnNodesSelected(null);
        marquee.setOnSpoolsSelected(null);
      };
    }

    marquee.setOnNodesSelected(null);
    marquee.setOnSpoolsSelected(null);
    return undefined;
  }, [
    isBulkPipeSelecting,
    isSelecting,
    isChangingPipes,
    isChangingPipeBetweenNodes,
    isConnectingNodes,
    isDisconnectingNodes,
    entityType,
    handleBulkSpoolPipeChange,
    marquee,
  ]);

  React.useEffect(() => {
    if (!selectedPipeType && isBulkPipeSelecting) {
      setIsBulkPipeSelecting(false);
      marquee.setMarqueeMode(false);
      invalidatePipeModel();
    }
  }, [selectedPipeType, isBulkPipeSelecting, marquee, invalidatePipeModel]);

  React.useEffect(() => {
    if (isConnectingNodes) {
      tools.setNodeClickHandler((node) => handleConnectNodeSelect(node));
      return () => tools.setNodeClickHandler(null);
    }
    if (isChangingPipeBetweenNodes) {
      tools.setNodeClickHandler((node) => handleChangePipeBetweenNodesSelect(node));
      return () => tools.setNodeClickHandler(null);
    }
    return undefined;
  }, [
    isConnectingNodes,
    isChangingPipeBetweenNodes,
    handleConnectNodeSelect,
    handleChangePipeBetweenNodesSelect,
    tools,
  ]);

  React.useEffect(() => {
    if (isChangingPipes) {
      tools.setSpoolClickHandler(handleChangePipeSpoolSelect);
      return () => tools.setSpoolClickHandler(null);
    }
    if (isDisconnectingNodes) {
      tools.setSpoolClickHandler(handleDisconnectSpoolSelect);
      return () => tools.setSpoolClickHandler(null);
    }
    if (isBulkPipeSelecting) {
      tools.setSpoolClickHandler(() => { });
      return () => tools.setSpoolClickHandler(null);
    }
    return undefined;
  }, [
    isChangingPipes,
    isDisconnectingNodes,
    isBulkPipeSelecting,
    handleChangePipeSpoolSelect,
    handleDisconnectSpoolSelect,
    tools,
  ]);

  React.useEffect(() => {
    const entityLabels: Record<string, string> = {
      All: i18n._('items'),
      Node: i18n._('nodes'),
      Spool: i18n._('spools'),
      Stope: i18n._('stopes'),
    };
    let message: string | null = null;
    if (isConnectingNodes) message = i18n._('Connect: click two nodes to create a spool between them');
    else if (isDisconnectingNodes) message = i18n._('Disconnect: click a spool to remove it');
    else if (isChangingPipes) message = i18n._('Change pipe: click a spool to apply "{pipe}"', { pipe: selectedPipeType });
    else if (isChangingPipeBetweenNodes) message = i18n._('Change pipe: pick two nodes to apply "{pipe}" along the path', { pipe: selectedPipeType });
    else if (isBulkPipeSelecting) message = i18n._('Change pipe: drag a box to apply "{pipe}" to the spools inside', { pipe: selectedPipeType });
    else if (isSelecting) message = i18n._('Select: drag a box to select {entities}', { entities: entityLabels[entityType] });
    updateBulkEditSceneState({ activeToolMessage: message });
  }, [
    isConnectingNodes,
    isDisconnectingNodes,
    isChangingPipes,
    isChangingPipeBetweenNodes,
    isBulkPipeSelecting,
    isSelecting,
    entityType,
    selectedPipeType,
  ]);

  React.useEffect(() => () => {
    const m = marqueeRef.current;
    m.setOnNodesSelected(null);
    m.setOnSpoolsSelected(null);
    m.setMarqueeMode(false);
    updateBulkEditSceneState({ isSelecting: false, activeToolMessage: null });
  }, []);

  return (
    <>
      <ToolbarSection label={i18n._('Select')}>
        <ToolbarButton
          label={(() => {
            if (isDeleting) return i18n._('Deleting...');
            if (isSelecting) return i18n._('Selecting...');
            return i18n._('Select');
          })()}
          tooltipTitle={i18n._('Marquee Select')}
          description={i18n._('Drag a box on the canvas to select nodes, spools or stopes of the chosen entity type. Press Escape to stop.')}
          icon={<HighlightAltIcon fontSize="small" />}
          onClick={handleToggleSelecting}
          selected={isSelecting}
          disabled={isDeleting}
          loading={isDeleting}
        />
      </ToolbarSection>
      <Divider orientation="vertical" flexItem />
      <ToolbarSection label={i18n._('Connect')}>
        <ToolbarButton
          label={i18n._('Connect')}
          tooltipTitle={i18n._('Connect Nodes')}
          description={i18n._('Pick two nodes in the canvas to create a spool between them. Click again to stop.')}
          icon={<LinkIcon fontSize="small" />}
          onClick={handleConnect}
          selected={isConnectingNodes}
        />
        <ToolbarButton
          label={i18n._('Disconnect')}
          tooltipTitle={i18n._('Disconnect Spool')}
          description={i18n._('Click a spool in the canvas to remove the connection between its two end nodes. Click again to stop.')}
          icon={<LinkOffIcon fontSize="small" />}
          onClick={handleDisconnect}
          selected={isDisconnectingNodes}
        />
      </ToolbarSection>
      <Divider orientation="vertical" flexItem />
      <ToolbarSection label={i18n._('Change Pipes')}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel
            id="pipe-type-select-label"
            sx={{ fontSize: 12, color: 'text.secondary', '&.Mui-focused': { color: 'text.secondary' } }}
          >
            {i18n._('Pipe Type')}
          </InputLabel>
          <Select
            labelId="pipe-type-select-label"
            label={i18n._('Pipe Type')}
            value={selectedPipeType}
            onChange={(e) => setSelectedPipeType(e.target.value as string)}
            color="secondary"
            sx={{ height: 32, fontSize: 13 }}
            MenuProps={{ sx: { zIndex: TOOLBAR_MENU_Z_INDEX } }}
          >
            {Object.values(model.pipes as Record<string, PCPipe>).map((pipe) => (
              <MenuItem key={pipe.name} value={pipe.name}>
                {pipe.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <SplitToolbarButton
          label={i18n._('Change Pipe')}
          selected={isPipeToolActive}
          tooltipTitle={pipeChangeTooltipTitle}
          description={pipeChangeDescription}
          disabledDescription={i18n._('Choose a pipe type first.')}
          icon={<SwapHorizIcon fontSize="small" />}
          onClick={handleChangePipe}
          onMenuItemClick={handlePipeChangeMenuItem}
          selectedKey={pipeChangeMode}
          disabled={!selectedPipeType}
          menuItems={[
            {
              key: 'bulk',
              label: i18n._('Bulk'),
              tooltipTitle: i18n._('Bulk Change Pipe'),
              description: i18n._('Marquee-select multiple spools at once and apply the chosen pipe type to all of them.'),
            },
            {
              key: 'spool',
              label: i18n._('Per Spool'),
              tooltipTitle: i18n._('Change Pipe per Spool'),
              description: i18n._('Click individual spools one at a time to apply the chosen pipe type.'),
            },
            {
              key: 'between',
              label: i18n._('Between Nodes'),
              tooltipTitle: i18n._('Change Pipe Between Nodes'),
              description: i18n._('Pick two nodes; every spool on the path between them gets the chosen pipe type.'),
            },
          ]}
        />
      </ToolbarSection>
      {canAutoAssignPipes && (
        <>
          <Divider orientation="vertical" flexItem />
          <ToolbarSection label={i18n._('Auto Assign')}>
            <ToolbarButton
              label={i18n._('Assign Pipes')}
              tooltipTitle={i18n._('Auto Assign Pipes')}
              description={i18n._('Steps overpressured spools on the routes to the chosen stopes up to the next higher-rated pipe. Shows a preview first.')}
              disabledDescription={i18n._('Available on pumped paste projects only.')}
              icon={<AutoFixHighIcon fontSize="small" />}
              onClick={() => {
                cancelAllToolModes();
                setAutoAssignOpen(true);
              }}
              disabled={!isPumpedPaste}
            />
          </ToolbarSection>
        </>
      )}
      <Divider orientation="vertical" flexItem />
      <ToolbarSection label={i18n._('Spool Status')}>
        <Box ref={statusFromDatesAnchorRef}>
          <ToolbarButton
            label={i18n._('From Dates')}
            tooltipTitle={i18n._('Set Spool Status From Dates')}
            description={i18n._('Set every spool Active or Inactive from its installed and removed dates as of a chosen date.')}
            icon={<DateRangeIcon fontSize="small" />}
            onClick={() => setStatusFromDatesOpen(true)}
            selected={statusFromDatesOpen}
          />
        </Box>
        <SpoolStatusFromDatesPopout
          open={statusFromDatesOpen}
          anchorEl={statusFromDatesAnchorRef.current}
          onClose={() => setStatusFromDatesOpen(false)}
          model={model}
        />
      </ToolbarSection>
      <Divider orientation="vertical" flexItem />
      <ToolbarSection label={i18n._('Group')}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={selectedGroupId}
            onChange={handleGroupChange}
            displayEmpty
            color="secondary"
            sx={{ height: 32, fontSize: 13 }}
            MenuProps={{ sx: { zIndex: TOOLBAR_MENU_Z_INDEX } }}
          >
            <MenuItem value="">
              <em>{i18n._('Select group')}</em>
            </MenuItem>
            {filteredGroups.map((group) => (
              <MenuItem key={group.groupId} value={group.groupId ?? ''}>
                {group.groupName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <ToolbarButton
          label={i18n._('Create')}
          tooltipTitle={i18n._('Create New Group')}
          description={i18n._('Save the current selection as a named group you can recall later.')}
          disabledDescription={i18n._('Select items to group first.')}
          icon={<GroupAddIcon fontSize="small" />}
          onClick={handleOpenCreateDialog}
          disabled={selectedIds.length === 0}
        />
        <ToolbarButton
          label={i18n._('Select')}
          tooltipTitle={i18n._('Select Group Members')}
          description={i18n._('Replace the current selection with all members of the chosen group.')}
          disabledDescription={i18n._('Choose a group first.')}
          icon={<PlaylistAddCheckIcon fontSize="small" />}
          onClick={handleSelectGroup}
          disabled={!selectedGroupId}
        />
        <ToolbarButton
          label={i18n._('Add')}
          tooltipTitle={i18n._('Add Selection to Group')}
          description={i18n._('Append the currently selected items to the chosen group.')}
          disabledDescription={i18n._('Choose a group and select items first.')}
          icon={<AddIcon fontSize="small" />}
          onClick={handleAddSelectionToGroup}
          disabled={!selectedGroupId || selectedIds.length === 0}
        />
        <ToolbarButton
          label={i18n._('Remove')}
          tooltipTitle={i18n._('Remove Selection from Group')}
          description={i18n._('Remove the currently selected items from the chosen group.')}
          disabledDescription={i18n._('Choose a group and select items first.')}
          icon={<RemoveIcon fontSize="small" />}
          onClick={handleRemoveSelectionFromGroup}
          disabled={!selectedGroupId || selectedIds.length === 0}
        />
      </ToolbarSection>
      <Divider orientation="vertical" flexItem />
      <ToolbarSection label={i18n._('Delete')}>
        <ToolbarButton
          label={i18n._('Delete')}
          tooltipTitle={i18n._('Delete Selection')}
          description={i18n._('Permanently remove every selected node, spool and stope.')}
          disabledDescription={i18n._('Select items to delete first.')}
          icon={<DeleteIcon fontSize="small" />}
          onClick={deleteSelected}
          disabled={selectedIds.length === 0 || isDeleting}
          loading={isDeleting}
        />
      </ToolbarSection>

      <Dialog open={isCreateDialogOpen} onClose={handleCloseCreateDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{i18n._('Create Group')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {i18n._('{count, plural, one {# item will be added to this group.} other {# items will be added to this group.}}', { count: selectedIds.length })}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            color="secondary"
            label={i18n._('Group name')}
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmCreateGroup(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>{i18n._('Cancel')}</Button>
          <Button
            onClick={handleConfirmCreateGroup}
            variant="contained"
            color="secondary"
            disabled={!newGroupName.trim() || selectedIds.length === 0}
          >
            {i18n._('Create')}
          </Button>
        </DialogActions>
      </Dialog>
      {autoAssignOpen && <AutoPipeAssignerDialog model={model} onClose={() => setAutoAssignOpen(false)} />}
    </>
  );
};

export default EditTab;
